import { Fragment, useSyncExternalStore } from 'react';
import { User, Monitor, Shield, Bell, Download, Mail, Inbox, type LucideIcon } from '../../icons';
import { Modal } from '../shared/Modal';
import { useModalLeave } from '../../hooks/useModalLeave';
import {
  useProfileUiStore, SETTINGS_CATEGORIES, SETTINGS_SUBS,
  type SettingsCategory, type SettingsSub,
} from '../../store/profileUiStore';
import {
  tSettings, getSettingsLang, subscribeSettingsLang, type SettingsI18nKey,
} from '../../lib/settings-i18n';
import { ProfilePanel } from './panels/ProfilePanel';
import { ThemePanel } from './panels/ThemePanel';
// 테마 설정(2026-08-20) — 환경설정 첫 화면. 옛 AppearanceSection 은 무수정 보존(/me·PreferencesPanel 이 쓴다).
import { ThemeSettingsPanel } from './panels/ThemeSettingsPanel';
import { PolicyPanel } from './panels/PolicyPanel';
// 앱 다운로드(2026-09-14 · 시안 axi-settings-download 확정) — 사내 배포 앱 받기. 옛 DownloadPanel(자리표시 셋)은 파일째 보존.
import { AppDownloadPanel } from '../AppDownload/AppDownloadPanel';
// 화면잠금(2026-08-19) — 보안설정의 하위 두 화면.
import { ScreenLockPanel } from './panels/ScreenLockPanel';
import { LockTimingPanel } from './panels/LockTimingPanel';
// 하위 메뉴 분리(2026-08-20) — 아래 조각들은 지금까지 PreferencesPanel/SecurityPanel/
// NotificationsPanel 이 한 화면에 묶어 두던 것을 그대로 가져다 쓴다(내용 무변경).
// 그 셋(합본 패널)은 **무수정 보존** — 되돌릴 때는 이 라우팅만 예전으로 돌리면 된다.
import {
  SecurityGeoPanel, SecurityDevicesPanel, SecurityMobilePanel,
} from './panels/SecurityMiscPanels';
import { NotifChannelsPanel } from './panels/NotifChannelsPanel';
// 내 알림(받은 알림 목록) — 2026-09-07 에 내 프로필에서 옮겨 왔다. 파일은 제자리에 두고 부르기만 한다.
import { MyNotificationsTab } from '../../pages/SettingsPage/MyNotificationsTab';
import { MailSettingsMoved } from './panels/MailSettingsMoved';
import { CalendarThemeTab } from '../../pages/SettingsPage/CalendarThemeTab';
import { LanguageField } from './LanguageField';
import { NotifKindsField } from './NotifKindsField';
import { ExternalProfileSection } from '../../pages/SettingsPage/ExternalProfileSection';
import { PasswordSection } from '../../pages/SettingsPage/PasswordSection';
import '../../pages/SettingsPage/SettingsPage.css';
import './SettingsModal.css';

/**
 * 설정 모달 — 2026-08-12 사용자 요구("설정에서 내정보관리·환경설정·보안설정·알림설정·다운로드").
 *
 * **왜 앱 내부 모달인가** — 사용자가 물은 그대로다: 별도 브라우저 창(`window.open`)으로 띄우면
 * 앞으로 설치형(PWA/Electron)으로 감쌀 때 팝업 차단·별창 분리로 못 쓰게 된다. 그래서 앱 위에
 * 뜨는 중앙 모달(공용 `Modal` 셸, all-frame.html #2e 규격)로 만든다 — 웹과 설치형이 같게 동작한다.
 *
 * 소유 관계
 *  - 열림 상태: `profileUiStore`(전역). 진입점(⑤ 레일 팝오버 · 대시보드 위젯)은 store 만 건드린다.
 *  - 본체 마운트: `AppLayout` 하나뿐 — 화면마다 자기 모달을 복제하지 않는다.
 *  - 내용: 기존 설정 섹션(ProfileSection · AppearanceSection · CalendarThemeTab · PasswordSection)을
 *    **그대로 재사용**한다. 같은 설정이 두 곳에서 따로 저장되는 이원화를 만들지 않기 위함이다.
 *
 * `/me` 페이지는 그대로 둔다(알림 드롭다운의 `?tab=notifications` 딥링크 등 기존 진입점 유지).
 */

interface CategoryDef { key: SettingsCategory; labelKey: SettingsI18nKey; icon: LucideIcon }

const CATEGORY_META: Record<SettingsCategory, CategoryDef> = {
  account: { key: 'account', labelKey: 'settings.modal.cat.account', icon: User },
  // 받은 알림 목록 — 옛 `/me?tab=notifications` 가 있던 자리다(2026-09-07 이관).
  inbox: { key: 'inbox', labelKey: 'settings.modal.cat.inbox', icon: Inbox },
  preferences: { key: 'preferences', labelKey: 'settings.modal.cat.preferences', icon: Monitor },
  security: { key: 'security', labelKey: 'settings.modal.cat.security', icon: Shield },
  mail: { key: 'mail', labelKey: 'settings.modal.cat.mail', icon: Mail },
  notifications: { key: 'notifications', labelKey: 'settings.modal.cat.notifications', icon: Bell },
  download: { key: 'download', labelKey: 'settings.modal.cat.download', icon: Download },
};

/** 하위 메뉴 라벨 — 순서는 SETTINGS_SUBS 가 SSOT. `pending` 은 아직 서버가 없는 화면. */
const SUB_META: Record<SettingsSub, { labelKey: SettingsI18nKey; isNew?: boolean; pending?: boolean }> = {
  // 내 정보 관리
  'profile': { labelKey: 'settings.profile.heading' },
  'policy': { labelKey: 'settings.modal.sub.policy' },
  // 환경설정
  'appearance': { labelKey: 'settings.themeset.heading', isNew: true },
  'theme': { labelKey: 'settings.theme.heading', isNew: true },
  'calendar': { labelKey: 'settings.caltheme.heading' },
  'language': { labelKey: 'settings.modal.language.heading' },
  // 보안설정
  'password': { labelKey: 'settings.password.heading' },
  'screen-lock': { labelKey: 'settings.modal.sub.screen_lock', isNew: true },
  'lock-timing': { labelKey: 'settings.modal.sub.lock_timing', isNew: true },
  'geo': { labelKey: 'settings.modal.security.geo_title', pending: true },
  'devices': { labelKey: 'settings.modal.security.sessions_title', pending: true },
  'mobile': { labelKey: 'settings.modal.sub.mobile', pending: true },
  // 메일 (2026-08-20) — mailacc 는 알림설정에서 옮겨 왔다.
  'mail-basic': { labelKey: 'settings.mailpref.basic', isNew: true },
  'mail-write': { labelKey: 'settings.mailpref.write', isNew: true },
  'mail-read': { labelKey: 'settings.mailpref.read', isNew: true },
  // ★ 'mailacc' 는 **메일 > 환경설정 > 외부메일** 로 옮겼다(2026-09-05 사용자 지시).
  //   메뉴에서 뺐지만 키는 남긴다 — 옛 주소(?sub=mailacc)로 들어온 사람에게 어디로 갔는지 알린다.
  'mailacc': { labelKey: 'settings.mailacc.sub' },
  // 알림설정
  'kinds': { labelKey: 'settings.modal.notif.kinds' },
  'external': { labelKey: 'settings.external.heading' },
  'channels': { labelKey: 'settings.modal.sub.channels' },
};

/**
 * 카테고리 + 하위 → 화면.
 *
 * 각 case 의 마지막 return 이 **하위가 생기기 전 그 카테고리를 열었을 때 보이던 첫 화면**이다
 * (환경설정=화면·외관, 보안설정=비밀번호 변경, 알림설정=알림 종류). 하위 키가 없거나 모르는
 * 값이어도 거기로 떨어지므로, 저장된 값이 낡아도 빈 화면이 되지 않는다.
 */
function Panel({ category, sub }: { category: SettingsCategory; sub: SettingsSub | null }) {
  switch (category) {
    case 'account':
      if (sub === 'policy') return <PolicyPanel />;
      return <ProfilePanel />;
    case 'preferences':
      if (sub === 'theme') return <ThemePanel />;
      if (sub === 'calendar') return <CalendarThemeTab />;
      if (sub === 'language') return <LanguageField />;
      // 첫 화면 = 테마 설정(완성 테마 12종). 되돌릴 땐 이 한 줄을 <AppearanceSection /> 로.
      return <ThemeSettingsPanel />;
    case 'security':
      if (sub === 'screen-lock') return <ScreenLockPanel />;
      if (sub === 'lock-timing') return <LockTimingPanel />;
      if (sub === 'geo') return <SecurityGeoPanel />;
      if (sub === 'devices') return <SecurityDevicesPanel />;
      if (sub === 'mobile') return <SecurityMobilePanel />;
      return <PasswordSection />;
    /*
     * 메일 (2026-09-06 사용자 지시) — 이 갈래는 통째로 **메일 > 환경설정**으로 옮겼다.
     * 메뉴에서는 뺐지만 case 는 남긴다: 옛 주소로 들어온 사람에게 **어디로 갔는지**를 알린다.
     */
    case 'mail':
      if (sub === 'mailacc') return <MailSettingsMoved tab="external" />;
      return <MailSettingsMoved tab="basic" />;
    case 'inbox': return <MyNotificationsTab />;
    case 'notifications':
      if (sub === 'external') return <ExternalProfileSection />;
      if (sub === 'channels') return <NotifChannelsPanel />;
      return <NotifKindsField />;
    case 'download': return <AppDownloadPanel />;
    default: return null;
  }
}

/** AppLayout 이 상시 마운트하는 호스트 — 열림 상태는 store 가 소유한다. */
export function SettingsModalHost() {
  const open = useProfileUiStore((s) => s.settingsOpen);
  const { shouldRender, isLeaving } = useModalLeave(open, 200);
  if (!shouldRender) return null;
  return <SettingsModal isLeaving={isLeaving} />;
}

export function SettingsModal({ isLeaving = false }: { isLeaving?: boolean }) {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const category = useProfileUiStore((s) => s.settingsCategory);
  const setCategory = useProfileUiStore((s) => s.setSettingsCategory);
  const subByCategory = useProfileUiStore((s) => s.settingsSub);
  const setSub = useProfileUiStore((s) => s.setSettingsSub);
  const sub = subByCategory[category];
  const close = useProfileUiStore((s) => s.closeSettings);

  return (
    <Modal title={tSettings('settings.modal.title')} size="xl" bodyFlat isLeaving={isLeaving} onClose={close}>
      <nav className="st-set__nav" aria-label={tSettings('settings.modal.nav_aria')}>
        {SETTINGS_CATEGORIES.map((key) => {
          const { labelKey, icon: Icon } = CATEGORY_META[key];
          const active = category === key;
          return (
            <Fragment key={key}>
              <button
                type="button"
                className={`st-set__item${active ? ' is-active' : ''}`}
                aria-current={active || undefined}
                onClick={() => setCategory(key)}
              >
                <Icon size={15} aria-hidden />
                <span>{tSettings(labelKey)}</span>
              </button>
              {/* 2단계 — 열려 있는 카테고리에 하위가 있으면 그 자리에서 펼쳐진다(P1안). */}
              {active && SETTINGS_SUBS[key].length > 0 && (
                <div className="st-set__sub" role="group" aria-label={tSettings('settings.modal.sub_aria')}>
                  {SETTINGS_SUBS[key].map((subKey) => {
                    const meta = SUB_META[subKey];
                    const on = subByCategory[key] === subKey;
                    return (
                      <button
                        key={subKey}
                        type="button"
                        className={`st-set__subitem${on ? ' is-active' : ''}${meta.pending ? ' is-pending' : ''}`}
                        aria-current={on || undefined}
                        onClick={() => setSub(key, subKey)}
                      >
                        <span className="st-set__sublabel">{tSettings(meta.labelKey)}</span>
                        {meta.isNew && <span className="st-set__new">{tSettings('settings.modal.sub.new')}</span>}
                        {meta.pending && (
                          <span className="st-set__dot" title={tSettings('settings.modal.pending_badge')} aria-hidden />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </Fragment>
          );
        })}
      </nav>

      <div className="st-set__body scrollbar-thin">
        <Panel category={category} sub={sub} />
      </div>
    </Modal>
  );
}
